import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInputProps,
  SxProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Theme } from '@mui/system';
import { useMemo, useState } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';

export type InputKitProps = {
  placeholder?: string;
  name: string;
  disabled?: boolean;
  validation?: RegisterOptions;
  sxStyle?: SxProps<Theme>;
  type?: string;
  InputProps?: Partial<OutlinedInputProps>;
  mask?: string | (string | RegExp)[];
} & TextFieldProps;

export const InputKit = ({
  placeholder,
  name,
  disabled = false,
  sxStyle,
  type,
  InputProps,
  mask,
  ...props
}: InputKitProps) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const inputValue = watch(name) || '';

  const onInputChange = (e: any) => {
    const { value } = e.target;

    const newValue = type === 'number' ? Number(value) : value;

    setValue(name, newValue);
  };

  const errorMessage: any = !!errors[name]?.message
    ? errors[name]?.message
    : '';

  const isError = !!errorMessage;

  const inputType = useMemo(() => {
    if (!!type) {
      if (type === 'password') {
        return !!showPassword ? 'text' : 'password';
      }
      return type;
    }
    return 'text';
  }, [type, showPassword]);

  const inputPlaceholder = useMemo(() => {
    if (!placeholder) {
      return type === 'number' ? 'Enter value' : 'Type here...';
    }
    return placeholder;
  }, [placeholder]);

  const input = (
    <TextField
      size='small'
      placeholder={inputPlaceholder}
      inputProps={{ type: inputType }}
      variant='outlined'
      disabled={disabled}
      error={isError}
      sx={sxStyle}
      helperText={errorMessage}
      aria-describedby='my-helper-text'
      value={inputValue}
      onChange={onInputChange}
      InputProps={{
        endAdornment: type === 'password' && (
          <InputAdornment position='end'>
            <IconButton
              aria-label='toggle password visibility'
              onClick={() => setShowPassword(!showPassword)}
              onMouseDown={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <VisibilityOutlined />
              ) : (
                <VisibilityOffOutlined />
              )}
            </IconButton>
          </InputAdornment>
        ),
        ...InputProps,
      }}
      {...props}
    />
  );

  return (
    <FormControl fullWidth error={isError} required={isError}>
      {!!mask ? (
        <InputMask
          mask={mask}
          value={inputValue}
          onChange={onInputChange}
          disabled={disabled}
        >
          {input}
        </InputMask>
      ) : (
        input
      )}
    </FormControl>
  );
};
